import {
  ErrorAnnotations,
  ErrorCatalogWire,
  FieldFormat,
  LocalizedText
} from '@core/models/error-catalog.model';

type Node = Record<string, unknown>;

function asNode(value: unknown): Node | undefined {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
    ? (value as Node)
    : undefined;
}

/** A `<lang>` map, kept only when every value is a string. */
function readLocalized(value: unknown): LocalizedText | undefined {
  const node = asNode(value);
  if (!node) return undefined;
  const text: LocalizedText = {};
  for (const [lang, message] of Object.entries(node)) {
    if (typeof message !== 'string') return undefined;
    text[lang] = message;
  }
  return Object.keys(text).length > 0 ? text : undefined;
}

const REF_PREFIX = '#/$defs/';

/**
 * Follows the two unwrapping steps schemars emits — a `$ref` into `$defs`, and the single
 * non-null branch of an `anyOf` — until the node stops changing. They occur in both orders
 * and more than once, because `Option<T>` and `TypedUidOpt<T>` each introduce one. Bounded
 * so a cyclic `$ref` terminates.
 */
function resolve(node: Node | undefined, defs: Node): Node | undefined {
  let current = node;
  for (let step = 0; step < 8 && current; step++) {
    const ref = current['$ref'];
    if (typeof ref === 'string' && ref.startsWith(REF_PREFIX)) {
      const target = asNode(defs[ref.slice(REF_PREFIX.length)]);
      if (!target || target === current) return current;
      current = target;
      continue;
    }
    const anyOf = current['anyOf'];
    if (Array.isArray(anyOf)) {
      const branches = anyOf
        .map(asNode)
        .filter((branch): branch is Node => !!branch && branch['type'] !== 'null');
      if (branches.length !== 1) return current;
      current = branches[0];
      continue;
    }
    return current;
  }
  return current;
}

/**
 * The details payload's own properties. DataLayer reshapes each entry so the top-level
 * `properties` are already the payload's fields; a root-error shape, whose sole property
 * is `details`, is unwrapped so this reads the same either way.
 */
function payloadProperties(root: Node, defs: Node): Node {
  const properties = asNode(root['properties']) ?? {};
  const keys = Object.keys(properties);
  if (keys.length !== 1 || keys[0] !== 'details') return properties;
  return asNode(resolve(asNode(properties['details']), defs)?.['properties']) ?? properties;
}

/**
 * Enum labels keyed by the **property** that holds the enum, not by the enum's type name:
 * `formatValue` looks a label up by the field it is rendering, and one enum type can sit
 * on several properties. An array field's enum sits on `items` rather than the property
 * itself, mirroring `readFormats`'s `items`-unwrap.
 */
function readEnumValues(
  root: Node,
  defs: Node
): Record<string, Record<string, LocalizedText>> | undefined {
  const byProperty: Record<string, Record<string, LocalizedText>> = {};
  for (const [property, node] of Object.entries(payloadProperties(root, defs))) {
    const source = asNode(asNode(node)?.['items']) ?? asNode(node);
    const values = asNode(resolve(source, defs)?.['x-i18n-values']);
    if (!values) continue;
    const byVariant: Record<string, LocalizedText> = {};
    for (const [variant, labels] of Object.entries(values)) {
      const localized = readLocalized(labels);
      if (localized) byVariant[variant] = localized;
    }
    if (Object.keys(byVariant).length > 0) byProperty[property] = byVariant;
  }
  return Object.keys(byProperty).length > 0 ? byProperty : undefined;
}

/**
 * `x-format` sits on the property itself — DataLayer writes it as a sibling of any `$ref` —
 * so this reads the property node rather than the resolved one. An array carries it on
 * `items`, because the format describes a value and not a container.
 */
function readFormats(root: Node, defs: Node): Record<string, FieldFormat> | undefined {
  const byProperty: Record<string, FieldFormat> = {};
  for (const [property, value] of Object.entries(payloadProperties(root, defs))) {
    const node = asNode(value);
    if (!node) continue;
    const source = asNode(node['items']) ?? node;
    const format = source['x-format'];
    if (typeof format !== 'string') continue;
    const reference = source['x-reference'];
    byProperty[property] =
      typeof reference === 'string' ? { format, reference } : { format };
  }
  return Object.keys(byProperty).length > 0 ? byProperty : undefined;
}

function readEntry(schema: string): ErrorAnnotations | undefined {
  let root: Node | undefined;
  try {
    root = asNode(JSON.parse(schema));
  } catch {
    // A malformed schema costs this code its annotations, not the whole catalog.
    return undefined;
  }
  if (!root) return undefined;

  const defs = asNode(root['$defs']) ?? {};
  const annotations: ErrorAnnotations = {
    title: readLocalized(root['x-i18n-title']),
    message: readLocalized(root['x-i18n-message']),
    enumValues: readEnumValues(root, defs),
    formats: readFormats(root, defs)
  };
  return annotations.title ||
    annotations.message ||
    annotations.enumValues ||
    annotations.formats
    ? annotations
    : undefined;
}

/**
 * Reads the annotations off every catalog entry that carries any. A code with none is
 * absent from the map rather than present-and-empty, so a caller's lookup answers the
 * question it is actually asking: does DataLayer describe this code's text?
 */
export function readAnnotations(wire: ErrorCatalogWire): Map<string, ErrorAnnotations> {
  const byCode = new Map<string, ErrorAnnotations>();
  for (const descriptor of wire.errors ?? []) {
    const { code, schema } = descriptor;
    if (typeof code !== 'string' || typeof schema !== 'string') continue;
    const annotations = readEntry(schema);
    if (annotations) byCode.set(code, annotations);
  }
  return byCode;
}
